import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService, ContractStatus, LoginUserToken } from '../../authentication.service';
import { Router, RouterLink } from '@angular/router';
import { FormValidationErrorHandler } from '@styleguide/form/form-validation-error-handler.service';
import { FormValidationErrors } from '@styleguide/form/form-validation-errors';
import { Observable } from 'rxjs';
import { HTTP_STATUS_OFFLINE } from '../../../http/httpError';
import { UserCredentialsStore } from '../../user-credentials-store.service';
import { StorageService } from '../../../app-state/storage.service';
import { ConfigService } from '../../../config/config.service';
import { ICustomerAppsConfig } from '../../../config/config.module';
import { AsyncPipe } from '@angular/common';
import { LoadingDirective } from '@styleguide/loading/loading.directive';
import { ErrorsComponent } from '@styleguide/error/errors/errors.component';
import { FormFieldComponent } from '@styleguide/form/form-field/form-field.component';
import { PasswordInputControlComponent } from '@styleguide/form/password-input-control/password-input-control.component';
import { CheckboxControlComponent } from '@styleguide/form/checkbox-control/checkbox-control.component';
import { SelectLoginUserComponent } from '../select-login-user/select-login-user.component';

const TAC_ACCEPTED_STORAGE_KEY = 'kgr_tac_accepted';

const validationMessages = {
  tac: {
    required: $localize`Die Zustimmung zu den App-Nutzungsbedingungen ist notwendig.`,
  },
};

@Component({
  selector: 'kgr-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingDirective,
    ReactiveFormsModule,
    RouterLink,
    ErrorsComponent,
    FormFieldComponent,
    PasswordInputControlComponent,
    CheckboxControlComponent,
    SelectLoginUserComponent,
    AsyncPipe,
  ],
})
export class LoginFormComponent implements OnInit {
  @HostBinding('class.kgr-animated')
  @HostBinding('class.kgr-block')
  cssDefaultClasses = true;

  @HostBinding('class.kgr-animated--rippleSuccess')
  get rippleSuccess(): boolean {
    return this.loginUserTokens.length > 0 && !this.unexpectedError && !this.errorBadCredentials;
  }

  @HostBinding('class.kgr-animated--rippleError')
  get rippleError(): boolean {
    return this.unexpectedError || this.errorBadCredentials;
  }

  @Output() loginResult = new EventEmitter<boolean>();
  @Output() loginSuccess = new EventEmitter<void>();

  @Input() organizationId?: number; // restrict login to this organization only
  @Input() redirectUrl?: string;
  @Input() redirectCommands?: string[];
  @Input() redirectReplaceUrl = false;
  @Input() showEvenIfAlreadyLoggedIn = false;

  @Input() presetUsername = '';

  public isLoading = false;

  form: UntypedFormGroup;
  userCredentials$ = this.userCredentialsStore.userCredentials$;

  unexpectedError = false;
  unexpectedServerError = false;
  errorBadCredentials = false;
  errorOffline = false;

  validationErrors$!: Observable<FormValidationErrors>;
  treatAllControlsAsDirty = false;

  loginUserTokens: LoginUserToken[] = [];

  termsAndConditionsUrl?: string;

  constructor(
    private cd: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private userCredentialsStore: UserCredentialsStore,
    private router: Router,
    private formValidationErrorHandler: FormValidationErrorHandler,
    private storageService: StorageService,
    private configService: ConfigService<ICustomerAppsConfig>,
  ) {}

  ngOnInit() {
    this.form = new UntypedFormGroup({
      username: new FormControl<string>(this.presetUsername, [Validators.required]),
      password: new FormControl<string>('', [Validators.required]),
    });

    this.termsAndConditionsUrl = this.configService.config.termsAndConditionsUrl;

    if (this.termsAndConditionsUrl) {
      this.storageService.getItemBoolean(TAC_ACCEPTED_STORAGE_KEY).subscribe((tacAccetpted) => {
        if (!tacAccetpted) {
          this.form.addControl('tac', new FormControl<boolean>(false, [Validators.requiredTrue]));
        }
      });
    }

    this.form.valueChanges.subscribe(() => {
      this.resetErrors();
    });
    this.validationErrors$ = this.formValidationErrorHandler.getErrorsStream(this.form, validationMessages);
  }

  onLogin() {
    this.treatAllControlsAsDirty = true;
    this.loginUserTokens = [];
    if (this.form.valid) {
      this.resetErrors();
      this.isLoading = true;
      this.authenticationService.fetchLoginUserTokens(this.form.value.username, this.form.value.password, this.organizationId).subscribe(
        (loginUserTokens) => {
          if (loginUserTokens.length === 0) {
            this.errorBadCredentials = true;
          } else if (
            loginUserTokens.length === 1 &&
            (loginUserTokens[0].contractStatus === null ||
              loginUserTokens[0].contractStatus === undefined ||
              loginUserTokens[0].contractStatus === ContractStatus.running)
          ) {
            if (loginUserTokens[0].token && this.authenticationService.loginByToken(loginUserTokens[0].token)) {
              this.onLoginSuccess();
            } else {
              this.unexpectedError = true;
            }
          } else {
            this.loginUserTokens = loginUserTokens;
          }
          this.isLoading = false;
          this.cd.markForCheck();
        },
        (apiError) => {
          console.log('error', apiError);
          switch (apiError.code) {
            case HTTP_STATUS_OFFLINE:
            case 0:
              this.errorOffline = true;
              break;
            case 404:
            case 401:
            case 403:
              this.errorBadCredentials = true;
              break;
            default:
              this.unexpectedServerError = true;
              break;
          }
          this.isLoading = false;
          this.cd.markForCheck();
        },
      );
    }
  }

  onLoginSuccess() {
    this.storageService.setItemBoolean(TAC_ACCEPTED_STORAGE_KEY, true);
    this.isLoading = false;
    this.loginResult.emit(true);
    this.loginSuccess.emit();
    if (this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl, { replaceUrl: this.redirectReplaceUrl });
    } else if (this.redirectCommands?.length > 0) {
      this.router.navigate(this.redirectCommands, { replaceUrl: this.redirectReplaceUrl });
    }
  }

  onMultiAccountLoginResult(success: boolean) {
    if (success) {
      this.onLoginSuccess();
    } else {
      this.cancelLogin();
    }
  }

  cancelLogin() {
    this.loginResult.emit(false);
    this.resetErrors();
    this.loginUserTokens = [];
  }

  private resetErrors(): void {
    this.unexpectedError = false;
    this.unexpectedServerError = false;
    this.errorBadCredentials = false;
    this.errorOffline = false;
  }
}
